import type { UseFormReturn } from 'react-hook-form';
import {
  Checkbox,
  Labeled,
  Select,
  TextAreaField,
  TextInputField,
} from '@sixui/ui';
import { useTranslation } from 'react-i18next';

import type { IContainerProps } from '~/components/utils/Container';
import type { IFormMode } from '~/helpers/types';
import type { IPollFormValues } from './PollForm';
import { FormDateField } from '~/components/atoms/fields/DateField';
import { FormField } from '~/components/atoms/FormField';
import { FormSection } from '~/components/atoms/FormSection';

export type IPollFormMainSectionProps = IContainerProps & {
  form: UseFormReturn<IPollFormValues>;
  mode: IFormMode;
};

export const PollFormMainSection: React.FC<IPollFormMainSectionProps> = (
  props,
) => {
  const { sx, form } = props;
  const { t } = useTranslation();

  return (
    <FormSection sx={sx}>
      <Labeled label={t('PollFormMainSection.fields.title.label')} required>
        <FormField
          as={TextInputField}
          forwardError
          control={form.control}
          name="title"
        />
      </Labeled>

      <Labeled label={t('PollFormMainSection.fields.description.label')}>
        <FormField
          as={TextInputField}
          forwardError
          control={form.control}
          name="description"
        />
      </Labeled>

      <Labeled
        label={t('PollFormMainSection.fields.options.label')}
        supportingText={t('PollFormMainSection.fields.options.supportingText')}
        required
      >
        <FormField
          as={TextAreaField}
          style={{
            // This is a hack for TextAreaField to work in Firefox.
            // TODO: Remove this hack once fixed in @sixui/ui.
            whiteSpace: 'revert',
          }}
          forwardError
          control={form.control}
          name="options"
          rows={4}
          resizable
        />
      </Labeled>

      <Labeled label={t('PollFormMainSection.fields.startDate.label')} required>
        <FormDateField name="startDate" control={form.control} />
      </Labeled>

      <Labeled label={t('PollFormMainSection.fields.endDate.label')} required>
        <FormDateField name="endDate" control={form.control} />
      </Labeled>

      <Labeled label={t('PollFormMainSection.fields.requires.label')} required>
        <FormField
          name="requires"
          control={form.control}
          as={Select}
          forwardError
          items={[
            {
              value: 'none',
              label: t(
                'PollFormMainSection.fields.requires.options.none.label',
              ),
              supportingText: t(
                'PollFormMainSection.fields.requires.options.none.supportingText',
              ),
            },
            {
              value: 'auth',
              label: t(
                'PollFormMainSection.fields.requires.options.auth.label',
              ),
              supportingText: t(
                'PollFormMainSection.fields.requires.options.auth.supportingText',
              ),
            },
          ]}
        />
      </Labeled>

      <Labeled
        label={t('PollFormMainSection.fields.displayResults.label')}
        supportingText={t(
          'PollFormMainSection.fields.displayResults.supportingText',
        )}
        labelPosition="right"
      >
        <FormField name="displayResults" control={form.control} as={Checkbox} />
      </Labeled>

      <Labeled
        label={t('PollFormMainSection.fields.multipleChoices.label')}
        supportingText={t(
          'PollFormMainSection.fields.multipleChoices.supportingText',
        )}
        labelPosition="right"
      >
        <FormField
          name="multipleChoices"
          control={form.control}
          as={Checkbox}
        />
      </Labeled>

      <Labeled
        label={t('PollFormMainSection.fields.canUpdate.label')}
        supportingText={t(
          'PollFormMainSection.fields.canUpdate.supportingText',
        )}
        labelPosition="right"
      >
        <FormField name="canUpdate" control={form.control} as={Checkbox} />
      </Labeled>
    </FormSection>
  );
};
